import type { FormDataType } from '@inertiajs/core';
import { useForm as useInertiaForm, type InertiaFormProps } from '@inertiajs/react';

/**
 * Exposes Inertia's `useForm` through the page SDK. Reach for this whenever a page
 * posts directly to an app route (e.g. a resource endpoint); use
 * `Sdk.hooks.useWorkflowForm` when the submission target is a Diagonal workflow.
 *
 * @returns The same shape as Inertia's `useForm` — `.data`, `.errors`, `.processing`,
 * `.setData(...)`, `.submit(...)`, `.post(...)`, etc.
 *
 * @example
 * ```tsx
 * return function Page() {
 *   const form = Sdk.useForm({ name: '', email: '' });
 *
 *   return (
 *     <form
 *       onSubmit={(e) => {
 *         e.preventDefault();
 *         form.post('/students');
 *       }}
 *     >
 *       <Sdk.Components.FormTextInput
 *         label="Name"
 *         value={form.data.name}
 *         onChange={(v) => form.setData('name', v)}
 *       />
 *       <Sdk.Components.FormTextInput
 *         label="Email"
 *         value={form.data.email}
 *         onChange={(v) => form.setData('email', v)}
 *       />
 *       <Sdk.Components.Button disabled={form.processing} type="submit">
 *         Save
 *       </Sdk.Components.Button>
 *     </form>
 *   );
 * };
 * ```
 */
export function useForm<TForm extends FormDataType<TForm>>(
  defaults: TForm
): InertiaFormProps<TForm> {
  return useInertiaForm<TForm>(defaults);
}
